import { ComponentRef, EnvironmentInjector, inject, Injectable, InjectionToken, Type, ViewContainerRef } from '@angular/core';
import { EntityBase } from '../_models/EntityBase';
import { IEntityMetaData } from '../_models/IEntityMetaData';
import { GlobalProviderRegistry } from '../decorators/DependencyInjectionRegistreyDecorators';
import { IListComponent } from '../components/_primitives/IListComponent';
import { DataService } from './DataService';
import { ApiRepository } from '../repositories/ApiRepository';
import { environment } from '@app/../environments/environment';
import { HttpClient } from '@angular/common/http';
import { MatwinsVariablesService } from './matwinsVariables.service';
import { ResponseHandlerService } from './ResponseHandlerService';
import { LookupService } from './LookupService';
import { BrowserIdService } from './browser-id.service';
import { Client } from '../models/matwins-manager/Client.model';
import { PointOfService } from '../models/matwins-tenant/PointOfService.model';
import { Tenant } from '../models/matwins-tenant/Tenant.model';
import { Trucker } from '../models/matwins-tenant/Trucker.model';

export interface EntityConstructor<T extends EntityBase> {
  new(): T;
  getMetaData(): IEntityMetaData;
}


@Injectable({ providedIn: 'root' })
export class DependencyInjectionProvider {
  // registry existante : map[ entityName -> map[ providerClass | token -> instance ] ]
  private registry = new Map<string, Map<Type<any> | InjectionToken<any>, any>>();

  private entityConstructors = new Map<string, any>();

  protected browserIdService = inject(BrowserIdService); 

  constructor(
    private http: HttpClient,
    private envInjector: EnvironmentInjector,
    private variablesService: MatwinsVariablesService,
    private responseHandler: ResponseHandlerService,
    private lookupService: LookupService
  ) {

    // Enregistremnt des constructeur 
    this.initializeEntityConstructors(); 


  }

  // ---------- helpers existants (register/get/getByMetaData) ----------
  register<U, T extends EntityBase>(
    providerType: Type<U> | InjectionToken<U>,
    entityType: EntityConstructor<T>,
    entityMetaData: IEntityMetaData,
    providerInstance: U
  ) {
    const entityName = entityMetaData.EntityName;
    if (!this.registry.has(entityName)) {
      this.registry.set(entityName, new Map());
    }
    this.registry.get(entityName)!.set(providerType, providerInstance);
  }

  get<U, T extends EntityBase>(providerType: Type<U> | InjectionToken<U>, entityType: EntityConstructor<T>): U {
    const entityName = entityType.getMetaData().EntityName;
    return this.getByProviderAndEntityName(providerType, entityName);
  }

  getByMetaData<U, T extends EntityBase>(providerType: Type<U> | InjectionToken<U>, metaData: IEntityMetaData): U {
    const entityName = metaData.EntityName;
    return this.getByProviderAndEntityName(providerType, entityName);
  }

  private getByProviderAndEntityName<U>(providerType: Type<U> | InjectionToken<U>, entityName: string): U {
    const entityProviders = this.registry.get(entityName);
    if (!entityProviders) throw new Error(`No providers registered for ${entityName}`);
    const provider = entityProviders.get(providerType);
    if (!provider) throw new Error(`${(providerType as any).name || providerType.toString()} not registered for ${entityName}`);
    return provider;
  }

  // ---------- repository & dataService factory (pour DataService<T> générique) ----------
  private RepositoryFactory<T extends EntityBase>(entityMetaData: IEntityMetaData) {
    const repo = new ApiRepository<T>(this.http, this.variablesService, this.responseHandler, this.browserIdService);
    repo.setMetaData(entityMetaData);
    repo.setBaseUrl(`${environment.apiBaseUrl}/api`);
    return repo;
  }

  /**
   * Récupère ou crée un DataService<T> pour l'entité donnée.
   * - Cherche dans le registry sous la clé DataService
   * - Si absent, le crée, l'enregistre et le retourne.
   */
  getOrCreateDataService<T extends EntityBase>(entityType: EntityConstructor<T>): DataService<T> {
    const meta = entityType.getMetaData();
    const entityName = meta.EntityName;
    const key = DataService as Type<any>;

    let map = this.registry.get(entityName);
    if (map && map.has(key)) {
      return map.get(key) as DataService<T>;
    }

    // pas encore créé -> instancier
    const repo = this.RepositoryFactory<T>(meta);
    const ds = new DataService<T>(repo, this.lookupService);

    if (!map) {
      map = new Map();
      this.registry.set(entityName, map);
    }
    map.set(key, ds);
    return ds;
  }

  // ---------- retrouver la ComponentClass enregistrée pour une entité ----------
  /**
   * Trouve la classe de composant (Type) enregistrée pour l'entité donnée.
   * Cherche dans GlobalProviderRegistry les entrées dont providerClass est un composant (ɵcmp présent).
   */
  getRegisteredComponentClassForEntity<T extends EntityBase>(entityType: EntityConstructor<T>): Type<IListComponent<T>> | undefined {
    const entityName = entityType.getMetaData().EntityName;
    const entry = GlobalProviderRegistry.find(e =>
      e.entityType !== 'ALL_ENTITIES' &&
      (e.entityType as EntityConstructor<T>) === entityType &&
      !!(e.providerClass as any).ɵcmp // c'est un component Angular
    );
    return entry ? (entry.providerClass as Type<IListComponent<T>>) : undefined;
  }

  /**
   * Crée le composant enregistré pour l'entité demandée dans le ViewContainerRef fourni.
   * Retourne le ComponentRef fortement typé.
   */
  createRegisteredComponentInContainer<T extends EntityBase>(
    entityType: EntityConstructor<T>,
    vcr: ViewContainerRef
  ): ComponentRef<IListComponent<T>> {
    const compClass = this.getRegisteredComponentClassForEntity(entityType);
    if (!compClass) throw new Error(`No component registered for ${entityType.getMetaData().EntityName}`);

    // createComponent via le VCR (attache au DOM au bon endroit)
    const compRef = vcr.createComponent<IListComponent<T>>(compClass, {
      environmentInjector: this.envInjector
    });

    // S'assurer que le composant a bien ses propriétés de type/metadata (si il les attend)
    const inst: any = compRef.instance;
    if ('entityMetaData' in inst) inst.entityMetaData = entityType.getMetaData();
    if ('ComponentEntityClass' in inst) inst.ComponentEntityClass = entityType;

    // enregistrer l'instance dans le registry pour réutilisation (providerType = compClass)
    this.register(compClass, entityType, entityType.getMetaData(), compRef.instance);

    return compRef;
  }

  private initializeEntityConstructors(): void {
    for (const entry of GlobalProviderRegistry) {
      if (typeof entry.entityType === 'function') {
        try {
          const meta = (entry.entityType as any).getMetaData();
          if (meta && meta.EntityName) {
            this.entityConstructors.set(meta.EntityName, entry.entityType);
          }
        } catch (e) {
          // ignorer les types qui n'ont pas getMetaData (services, etc.)
        }
      }
    }
  }
}


